/**
 * CurrencyInput Component
 *
 * STANDARD UX PATTERNS FOR ALL CURRENCY/NUMBER INPUTS:
 * 1. ✅ Allow free typing - users can type, paste, edit without restrictions
 * 2. ✅ Focus behavior - select all text when focused for easy replacement
 * 3. ✅ Format on blur - only format when user finishes editing (leaves field)
 * 4. ✅ No spinners/steppers - avoid increment/decrement buttons that restrict input
 * 5. ✅ Smart parsing - handle various input formats gracefully
 * 6. ✅ Visual feedback - show currency symbol and code for context
 * 7. ✅ Keyboard support - allow all relevant keys (numbers, decimal, copy/paste)
 * 8. ✅ Constraint enforcement - apply min/max only on blur, not during typing
 *
 * This component should be used as the standard for all currency inputs across the app.
 * For non-currency numbers, use NumberInput with the same UX patterns.
 */

import React, { useState, useRef, forwardRef } from 'react';
import { formatCurrencyInput, parseCurrencyInput, getAppConfig } from '@/utils/configUtils';

interface CurrencyInputProps {
  label?: string;
  placeholder?: string;
  value?: number;
  onChange?: (value: number) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  min?: number;
  max?: number;
}

const CurrencyInput = forwardRef<HTMLInputElement, CurrencyInputProps>(
  (
    {
      label,
      placeholder = '0.00',
      value = 0,
      onChange,
      error,
      hint,
      required = false,
      disabled = false,
      className = '',
      min = 0,
      max,
    },
    ref
  ) => {
    const config = getAppConfig();
    const [displayValue, setDisplayValue] = useState(formatCurrencyInput(value));
    const [isFocused, setIsFocused] = useState(false);
    const inputRef = useRef<HTMLInputElement>(null);

    // Use forwarded ref or internal ref
    const actualRef = ref || inputRef;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(true);
      // Select all text when focused
      e.target.select();
    };

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
      setIsFocused(false);
      const numericValue = parseCurrencyInput(e.target.value);

      // Apply min/max constraints
      let constrainedValue = numericValue;
      if (min !== undefined && constrainedValue < min) {
        constrainedValue = min;
      }
      if (max !== undefined && constrainedValue > max) {
        constrainedValue = max;
      }

      // Format the display value
      const formattedValue = formatCurrencyInput(constrainedValue);
      setDisplayValue(formattedValue);

      // Call onChange with the numeric value
      if (onChange && constrainedValue !== value) {
        onChange(constrainedValue);
      }
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = e.target.value;

      // Allow typing while focused - don't format until blur
      if (isFocused) {
        setDisplayValue(inputValue);

        // Still call onChange for immediate validation if needed
        const numericValue = parseCurrencyInput(inputValue);
        if (onChange && !isNaN(numericValue)) {
          onChange(numericValue);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      // Allow: backspace, delete, tab, escape, enter, decimal point, minus
      if (
        [8, 9, 27, 13, 46, 110, 190].indexOf(e.keyCode) !== -1 ||
        // Allow: Ctrl+A, Ctrl+C, Ctrl+V, Ctrl+X
        (e.keyCode === 65 && e.ctrlKey === true) || // Ctrl+A
        (e.keyCode === 67 && e.ctrlKey === true) || // Ctrl+C
        (e.keyCode === 86 && e.ctrlKey === true) || // Ctrl+V
        (e.keyCode === 88 && e.ctrlKey === true) || // Ctrl+X
        // Allow: home, end, left, right
        (e.keyCode >= 35 && e.keyCode <= 39)
      ) {
        return;
      }

      // Ensure that it is a number or decimal point and stop the keypress
      if ((e.shiftKey || e.keyCode < 48 || e.keyCode > 57) && (e.keyCode < 96 || e.keyCode > 105)) {
        e.preventDefault();
      }
    };

    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      // Allow paste, let the change handler deal with validation
      setTimeout(() => {
        const pastedValue = (e.target as HTMLInputElement).value;
        const numericValue = parseCurrencyInput(pastedValue);
        if (!isNaN(numericValue)) {
          setDisplayValue(pastedValue);
          if (onChange) {
            onChange(numericValue);
          }
        }
      }, 0);
    };

    // Update display value when value prop changes (external updates)
    React.useEffect(() => {
      if (!isFocused && value !== undefined) {
        setDisplayValue(formatCurrencyInput(value));
      }
    }, [value, isFocused]);

    return (
      <div className="w-full font-['Inter',sans-serif]">
        <style>
          {`
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
          `}
        </style>

        {label && (
          <label className="block text-base font-medium text-[#1F1F1F] mb-2 leading-[1.75em]">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <div className="relative">
          {/* Currency Symbol */}
          <div className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#6E7077] text-base font-medium leading-[1.75em]">
            {config.currency.symbol}
          </div>

          <input
            ref={actualRef}
            type="text"
            inputMode="decimal"
            value={displayValue}
            onChange={handleChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
            placeholder={placeholder}
            disabled={disabled}
            className={`w-full pl-8 pr-16 py-3 border rounded-lg focus:ring-2 focus:outline-none transition-all duration-200 text-base font-normal leading-[1.75em] ${
              error
                ? 'border-red-300 focus:ring-red-500 focus:border-red-500'
                : 'border-gray-300 focus:ring-[var(--brand-primary)] focus:border-[var(--brand-primary)]'
            } ${
              disabled
                ? 'bg-gray-50 text-[#6E7077] cursor-not-allowed'
                : 'bg-white hover:border-[var(--brand-primary)] text-[#1F1F1F]'
            } focus:ring-opacity-20 placeholder-[#6E7077] ${className}`}
          />

          {/* Currency Code */}
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2 text-[#6E7077] text-sm font-medium leading-[1.75em]">
            {config.currency.code}
          </div>
        </div>

        {error && <p className="mt-2 text-sm text-red-600 font-normal leading-[1.75em]">{error}</p>}
        {hint && !error && (
          <p className="mt-2 text-sm text-[#6E7077] font-normal leading-[1.75em]">{hint}</p>
        )}
      </div>
    );
  }
);

CurrencyInput.displayName = 'CurrencyInput';

export default CurrencyInput;
